import {
	IMPORT_RULE_MATCHES_LOADED,
	IMPORT_RULES_ERROR_DISMISSED,
	IMPORT_RULES_LOAD_BEGIN,
	IMPORT_RULES_LOAD_FAIL,
	IMPORT_RULES_LOADED,
	IMPORT_RULES_WRITE_BEGIN,
	IMPORT_RULES_WRITE_FAIL,
} from '../actions/importRules.js';
import { IMPORT_MONARCH_RULES_LOADED } from '../actions/import.js';
import { CHANGE_ACTIVE_BUDGET_SUCCESS } from '../actions/sharing.js';

const initialState = {
	/** Every rule in the order it is tried, as `ordered_rules` lists them; null until first loaded. */
	rules: null,
	/**
	 * The import's account maps, as the listing gives them (`{ external_key, plaid_account_id, action }`):
	 * which account here each of the import's account names goes to. A rule names an account by that
	 * name, so this is how a rule is tied to an account. Null until first loaded.
	 */
	accountRules: null,
	/** The budget categories a rule can name, each with its group, as the listing gives them. */
	availableCategories: [],
	/** The budget's accounts an account map can name, as the listing gives them, for drawing a rule's account. */
	availableAccounts: [],
	/**
	 * What Import_Domain::rule_matches counted: `byId` maps a rule id to `{ matches, decides }`. Null
	 * until counted, and after any write until counted again, so no stale count is shown.
	 */
	matches: null,
	/** Counts each listing kept, so counts asked for against an older one can be told apart. */
	listingVersion: 0,
	loading: false,
	/**
	 * The newest load asked for (loadImportRules' `request`), or null once a write's listing has
	 * overtaken every load in flight. A load's answer is kept only while it is still the newest.
	 */
	loadRequest: null,
	/** A write is in flight; the list's buttons hold off so two moves cannot cross. */
	writing: false,
	/** Why the list could not be loaded. A refused write is answered to whoever asked for it. */
	error: null,
};

export default (state = initialState, action) => {
	switch(action.type) {
		case IMPORT_RULES_LOAD_BEGIN:
			return { ...state, loading: true, error: null, loadRequest: action.request === undefined ? state.loadRequest : action.request };

		case IMPORT_RULES_LOADED:
			if(isStale(state, action)) {
				return state;
			}
			return {
				...state,
				loadRequest: null,
				loading: false,
				writing: false,
				rules: action.payload.ordered_rules || [],
				accountRules: action.payload.account_rules || state.accountRules || [],
				availableCategories: action.payload.available_categories || state.availableCategories,
				availableAccounts: action.payload.available_accounts || state.availableAccounts,
				matches: null,
				listingVersion: state.listingVersion + 1,
			};

		case IMPORT_RULES_LOAD_FAIL:
			if(isStale(state, action)) {
				return state;
			}
			return { ...state, loading: false, error: action.payload, loadRequest: null };

		case IMPORT_RULES_WRITE_BEGIN:
			return { ...state, writing: true };

		case IMPORT_RULES_WRITE_FAIL:
			return { ...state, writing: false };

		case IMPORT_RULE_MATCHES_LOADED: {
			if(action.listingVersion !== undefined && action.listingVersion !== state.listingVersion) {
				return state;
			}
			const byId = {};
			(action.payload.rule_matches || []).forEach((count) => {
				byId[count.rule_id] = { matches: count.matches, decides: count.decides };
			});
			return {
				...state,
				matches: {
					byId,
					rowCount: action.payload.row_count || 0,
					undecidedCount: action.payload.undecided_count || 0,
				},
			};
		}

		/** An account map saved from the accounts page answers with every map, as the listing does. */
		case IMPORT_MONARCH_RULES_LOADED:
			if(!action.payload || !action.payload.account_rules) {
				return state;
			}
			return { ...state, accountRules: action.payload.account_rules };

		case IMPORT_RULES_ERROR_DISMISSED:
			return { ...state, error: null };

		/** Every rule belonged to the budget before. */
		case CHANGE_ACTIVE_BUDGET_SUCCESS:
			return initialState;

		default:
			return state;
	}
};

/** A load's answer that a newer load, or a write's listing, has overtaken. A write's own (no `request`) never is. */
function isStale(state, action) {
	return action.request !== undefined && action.request !== state.loadRequest;
}
